import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Avatar,
  Container,
  Paper,
  Chip
} from "@mui/material";
import {
  QrCode2 as QRCodeIcon,
  Timer as TimerIcon,
  People as PeopleIcon,
  Assignment as TaskIcon,
  Approval as ApprovalIcon,
  LocationOn as LocationIcon,
  Dashboard as DashboardIcon,
  TrendingUp as ReportIcon,
  SupervisorAccount as SupervisorIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon
} from "@mui/icons-material";
import { useUserContext } from "../context/UserContext";
import CustomAppBar from "../components/CustomAppBar";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userRole, selectedTenantName } = useUserContext();

  const adminActions = [
    {
      title: "Classroom Management",
      description: "Create classrooms and print static QR",
      icon: <DashboardIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      action: () => navigate('/classrooms'),
      color: "#e3f2fd"
    },
    {
      title: "Generate QR Event",
      description: "Create QR events for attendance tracking",
      icon: <QRCodeIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      action: () => navigate('/qr-event'),
      color: "#e3f2fd"
    },
    {
      title: "Event Management",
      description: "View, edit, and delete QR events",
      icon: <QRCodeIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      action: () => navigate('/event-management'),
      color: "#f3e5f5"
    },
    {
      title: "Timer Management",
      description: "Monitor and manage active timers",
      icon: <TimerIcon sx={{ fontSize: 40, color: "#388e3c" }} />,
      action: () => navigate('/timermanagement'),
      color: "#e8f5e8"
    },
    {
      title: "User Management",
      description: "Manage users and permissions",
      icon: <PeopleIcon sx={{ fontSize: 40, color: "#f57c00" }} />,
      action: () => navigate('/usermanagement'),
      color: "#fff3e0"
    },
    {
      title: "Assign Tasks",
      description: "Assign tasks to users",
      icon: <TaskIcon sx={{ fontSize: 40, color: "#7b1fa2" }} />,
      action: () => navigate('/task'),
      color: "#f3e5f5"
    },
    {
      title: "Leave Approval",
      description: "Review and approve leave requests",
      icon: <ApprovalIcon sx={{ fontSize: 40, color: "#d32f2f" }} />,
      action: () => navigate('/leaveapproval'),
      color: "#ffebee"
    },
    {
      title: "Assign Coordinates",
      description: "Set user location coordinates",
      icon: <LocationIcon sx={{ fontSize: 40, color: "#455a64" }} />,
      action: () => navigate('/assign'),
      color: "#eceff1"
    },
    {
      title: "Supervisor Management",
      description: "Assign supervisors and manage hierarchy",
      icon: <SupervisorIcon sx={{ fontSize: 40, color: "#1565c0" }} />,
      action: () => navigate('/supervisor-management'),
      color: "#e3f2fd"
    },
    {
      title: "Time Booking Approval",
      description: "Approve or reject time bookings from supervised users",
      icon: <CheckCircleIcon sx={{ fontSize: 40, color: "#2e7d32" }} />,
      action: () => navigate('/time-booking-approval'),
      color: "#e8f5e8"
    },
    {
      title: "Team Management",
      description: "Create, edit, and manage teams",
      icon: <GroupsIcon sx={{ fontSize: 40, color: "#1565c0" }} />,
      action: () => navigate('/formteam'),
      color: "#e3f2fd"
    },
    {
      title: "Tenant Management",
      description: "Create, edit, and manage teams",
      icon: <GroupsIcon sx={{ fontSize: 40, color: "#1565c0" }} />,
      action: () => navigate('/tenantmanagement'),
      color: "#e3f2fd"
    }
  ];

  const userActions = [
    {
      title: "Scan QR",
      description: "Scan QR code to join event",
      icon: <QRCodeIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      action: () => navigate('/scan'),
      color: "#e3f2fd"
    },
    {
      title: "View Attendance",
      description: "Check your attendance records",
      icon: <ReportIcon sx={{ fontSize: 40, color: "#388e3c" }} />,
      action: () => navigate('/report'),
      color: "#e8f5e8"
    },
    {
      title: "Time Booking",
      description: "Start/stop time tracking",
      icon: <TimerIcon sx={{ fontSize: 40, color: "#f57c00" }} />,
      action: () => navigate('/timer'),
      color: "#fff3e0"
    },
    {
      title: "Apply Leave",
      description: "Submit leave requests",
      icon: <ApprovalIcon sx={{ fontSize: 40, color: "#7b1fa2" }} />,
      action: () => navigate('/applyleave'),
      color: "#f3e5f5"
    }
  ];

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      py: 4
    }}>
      <CustomAppBar />
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: "#1976d2", mr: 2 }}>
              <DashboardIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Admin Dashboard
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Welcome, {userRole === "SuperAdmin" ? "Super Admin" : "Admin"}
              </Typography>
              {selectedTenantName && (
                <Chip 
                  label={`Tenant: ${selectedTenantName}`} 
                  color="primary" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Box>
        </Paper>

        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white", fontWeight: "bold" }}>
          Admin Functions
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {adminActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: "100%",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6
                  },
                  borderRadius: 3
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Box sx={{ mb: 2, p: 2, backgroundColor: action.color, borderRadius: 2 }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white", fontWeight: "bold" }}>
          Personal Functions
        </Typography>
        <Grid container spacing={3}>
          {userActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: "100%",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6
                  },
                  borderRadius: 3
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Box sx={{ mb: 2, p: 2, backgroundColor: action.color, borderRadius: 2 }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
